// Mensagens do Better Auth em pt-BR (`spec/17` §4.3: decisão por `code`, NUNCA por
// `message`). O Better Auth responde `{ code, message }` no corpo cru, com o código em
// SCREAMING_SNAKE e a mensagem em inglês curto, que não é contrato de UI. Os códigos
// vêm de `@better-auth/core` (`dist/error/codes.mjs`) e dos plugins `two-factor`,
// `captcha` e `admin` da versão 1.7.4.
//
// SCREAMING_SNAKE nunca colide com os códigos da nossa API (`spec/00` §4.3 usa
// snake_case minúsculo), então o mapa pode morar no caminho comum de `errorMessageOf`.

const MESSAGES: Record<string, string> = {
  // Credenciais e conta
  INVALID_EMAIL_OR_PASSWORD: 'Email ou senha incorretos.',
  INVALID_USERNAME_OR_PASSWORD: 'Email ou senha incorretos.',
  INVALID_PASSWORD: 'Senha incorreta.',
  INVALID_EMAIL: 'Email inválido.',
  INVALID_USER: 'Conta inválida.',
  USER_NOT_FOUND: 'Não encontramos uma conta com esse email.',
  USER_EMAIL_NOT_FOUND: 'Não encontramos uma conta com esse email.',
  ACCOUNT_NOT_FOUND: 'Não encontramos essa conta.',
  CREDENTIAL_ACCOUNT_NOT_FOUND: 'Esta conta não tem senha cadastrada.',
  USER_ALREADY_EXISTS: 'Já existe uma conta com esse email.',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'Já existe uma conta com esse email.',
  FAILED_TO_CREATE_USER: 'Não deu para criar a conta. Tente de novo.',
  FAILED_TO_UPDATE_USER: 'Não deu para salvar. Tente de novo.',
  BANNED_USER: 'Esta conta está bloqueada. Fale com o suporte.',

  // Senha
  PASSWORD_TOO_SHORT: 'A senha precisa de pelo menos 8 caracteres.',
  PASSWORD_TOO_LONG: 'A senha é longa demais.',
  PASSWORD_COMPROMISED: 'Essa senha apareceu em vazamentos conhecidos. Escolha outra.',
  PASSWORD_ALREADY_SET: 'Esta conta já tem senha cadastrada.',
  USER_ALREADY_HAS_PASSWORD: 'Esta conta já tem senha cadastrada.',

  // Email e links
  EMAIL_NOT_VERIFIED: 'Confirme seu email antes de entrar.',
  EMAIL_ALREADY_VERIFIED: 'Esse email já está confirmado.',
  EMAIL_MISMATCH: 'O email não confere.',
  INVALID_TOKEN: 'Este link é inválido ou já foi usado. Peça outro.',
  TOKEN_EXPIRED: 'Este link expirou. Peça outro.',
  FAILED_TO_CREATE_VERIFICATION: 'Não deu para gerar o link. Tente de novo.',
  VERIFICATION_EMAIL_NOT_ENABLED: 'A confirmação por email não está disponível.',

  // Sessão
  SESSION_EXPIRED: 'Sua sessão expirou. Entre de novo.',
  SESSION_NOT_FRESH: 'Por segurança, entre de novo antes desta ação.',
  FAILED_TO_CREATE_SESSION: 'Não deu para iniciar a sessão. Tente de novo.',
  FAILED_TO_GET_SESSION: 'Não deu para ler a sessão. Tente de novo.',
  INVALID_SESSION_TOKEN: 'Sessão inválida. Entre de novo.',

  // Segundo fator
  INVALID_CODE: 'Código inválido.',
  INVALID_OTP: 'Código inválido.',
  INVALID_BACKUP_CODE: 'Código de recuperação inválido.',
  INVALID_TWO_FACTOR_COOKIE: 'A verificação em duas etapas expirou. Entre de novo.',
  TWO_FACTOR_NOT_ENABLED: 'A verificação em duas etapas não está ligada nesta conta.',
  TOTP_NOT_ENABLED: 'O aplicativo autenticador não está configurado nesta conta.',
  TOTP_NOT_CONFIGURED: 'O aplicativo autenticador não está configurado nesta conta.',
  TOTP_ALREADY_ENABLED: 'A verificação em duas etapas já está ligada.',
  BACKUP_CODES_NOT_ENABLED: 'Os códigos de recuperação não estão disponíveis nesta conta.',
  OTP_EXPIRED: 'O código expirou. Peça outro.',
  OTP_HAS_EXPIRED: 'O código expirou. Peça outro.',

  // Limites
  TOO_MANY_ATTEMPTS: 'Tentativas demais. Espere um pouco e tente de novo.',
  TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE: 'Tentativas demais. Peça um código novo.',
  ACCOUNT_TEMPORARILY_LOCKED:
    'Tentativas demais: a conta ficou bloqueada por alguns minutos. Tente de novo mais tarde.',

  // Captcha (plugin `captcha`, SEC-01)
  MISSING_RESPONSE: 'Verificação expirou, tente de novo.',
  VERIFICATION_FAILED: 'Verificação expirou, tente de novo.',
  SERVICE_UNAVAILABLE: 'A verificação anti-robô não respondeu. Tente de novo.',

  // Origem e destino
  INVALID_CALLBACK_URL: 'Endereço de retorno não autorizado. Fale com o suporte.',
  INVALID_REDIRECT_URL: 'Endereço de retorno não autorizado. Fale com o suporte.',
  INVALID_ORIGIN: 'Origem não autorizada. Fale com o suporte.',
  CROSS_SITE_NAVIGATION_LOGIN_BLOCKED: 'Entre a partir da própria página de login.',
  CALLBACK_URL_REQUIRED: 'Endereço de retorno ausente. Fale com o suporte.',

  // Formulário
  VALIDATION_ERROR: 'Confira os campos e tente de novo.',
  MISSING_FIELD: 'Preencha todos os campos obrigatórios.',
  BODY_MUST_BE_AN_OBJECT: 'Não deu para enviar o formulário. Tente de novo.',
  FIELD_NOT_ALLOWED: 'Campo não permitido.',

  // Genéricos
  UNEXPECTED_ERROR: 'Algo deu errado. Tente de novo.',
  UNKNOWN_ERROR: 'Algo deu errado. Tente de novo.',
};

/** `undefined` quando o código não é do Better Auth: quem chama cai na mensagem da API. */
export function authErrorMessage(code: string): string | undefined {
  return MESSAGES[code];
}
